import { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Box, CircularProgress } from "@mui/material"
import Grid from "@mui/material/Grid"
import type { AxiosError } from "axios"

import { KonanTabsHeader } from "../../components/core/KonanTabsHeader"
import { KonanEmptyState, KonanEmptyStateSearch } from "../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../services/KonanAPI"
import type { TrainingDataUpload } from "../../types/generated/api/TrainingDataUpload"
import { mapResponseTypesToTabValues } from "../../utils/deploymentDetailsHelpers"
import { searchListByName, sortList } from "../../utils/searchSortFilterHelpers"
import { ProcessingFailedCard } from "./components/ProcessingFailedCard"
import { ProcessingFailedLoader } from "./components/ProcessingFailedLoader"
import { TrainingDataCard } from "./components/TrainingDataCard"
import { TrainingDataLoader } from "./components/TrainingDataLoader"
import { UploadCSVDialog } from "./components/UploadCSVDialog"

import styles from "./TrainingData.module.scss"

export function TrainingData(): React.ReactElement {
  const { t } = useTranslation("TrainingData")
  const pageSize = 6

  const { id: projectId } = useParams<{ id: string }>()

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastTrainingDataRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasDataLeft && !isTrainingDataLoading && loadMoreTrainingData()
    },
  })

  const [searchValue, setSearchValue] = useState<string>("")
  const [sortValue, setSortValue] = useState<string>("Most Recent")
  const [sortValueFailed, setSortValueFailed] = useState<string>("Most Recent")
  const [sortValueInProgress, setSortValueInProgress] = useState<string>("Most Recent")
  const [searchValueFailed, setSearchValueFailed] = useState<string>("")
  const [searchValueInProgress, setSearchValueInProgress] = useState<string>("")
  const [currentPage, setCurrentPage] = useState(1)
  const [uploadCSVDialogOpen, setUploadCSVDialogOpen] = useState(false)
  const [dialogKey, setDialogKey] = useState(0)
  const [uploadedDataId, setUploadedDataId] = useState("")
  const [tabValue, setTabValue] = useState<string>("success")

  //fetch all training data on a project level
  const { isLoading: isTrainingDataLoading, data: trainingData } = useQuery<TrainingDataUpload[], AxiosError>(
    ["training-data", projectId],
    () => KonanAPI.fetchTrainingData(projectId as string),
    { enabled: !!projectId },
  )

  const succeededTrainingData = trainingData?.filter((item) => item?.status === "success")
  const failedTrainingData = trainingData?.filter((item) => item?.status === "error")
  const processingTrainingData = trainingData?.filter(
    (item) => item?.status === "processing" || item?.status === "pending",
  )

  const adjustedTrainings = useMemo((): TrainingDataUpload[] => {
    let adjustedTrainings = succeededTrainingData || []
    if (searchValue && searchValue.length > 0)
      adjustedTrainings = adjustedTrainings ? searchListByName(adjustedTrainings, searchValue) : []

    if (sortValue && sortValue.length > 0)
      adjustedTrainings = adjustedTrainings ? sortList(adjustedTrainings, sortValue, "training-data") : []

    return adjustedTrainings
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [succeededTrainingData, searchValue, sortValue, trainingData?.length])

  const adjustedFailedTrainings = useMemo((): TrainingDataUpload[] => {
    let adjustedTrainings = failedTrainingData || []
    if (searchValueFailed && searchValueFailed.length > 0)
      adjustedTrainings = adjustedTrainings ? searchListByName(adjustedTrainings, searchValueFailed) : []
    if (sortValueFailed && sortValueFailed.length > 0)
      adjustedTrainings = adjustedTrainings ? sortList(adjustedTrainings, sortValueFailed, "training-data") : []

    return adjustedTrainings
  }, [failedTrainingData, searchValueFailed, sortValueFailed])

  const adjustedInProgressTrainings = useMemo((): TrainingDataUpload[] => {
    let adjustedTrainings = processingTrainingData || []
    if (searchValueInProgress && searchValueInProgress.length > 0)
      adjustedTrainings = adjustedTrainings ? searchListByName(adjustedTrainings, searchValueInProgress) : []
    if (sortValueInProgress && sortValueInProgress.length > 0)
      adjustedTrainings = adjustedTrainings ? sortList(adjustedTrainings, sortValueInProgress, "training-data") : []

    return adjustedTrainings
  }, [processingTrainingData, searchValueInProgress, sortValueInProgress])

  const hasDataLeft =
    (tabValue === "success" && adjustedTrainings && adjustedTrainings.length >= currentPage * pageSize) ||
    (tabValue === "failed" && adjustedFailedTrainings && adjustedFailedTrainings.length >= currentPage * pageSize) ||
    (tabValue === "inProgress" &&
      adjustedInProgressTrainings &&
      adjustedInProgressTrainings.length >= currentPage * pageSize)

  const loadMoreTrainingData = (): void => {
    setCurrentPage(currentPage + 1)
  }

  const handleUploadCSVDialogOpen = (): void => {
    setUploadCSVDialogOpen(true)
    setDialogKey((dialogKey + 1) % 10)
  }

  const handleUploadCSVDialogClose = (): void => {
    setUploadCSVDialogOpen(false)
  }

  useEffect(() => {
    if (uploadedDataId) {
      const filtered = trainingData?.filter((train) => train?.uuid === uploadedDataId)
      if (filtered?.length) {
        setTabValue(mapResponseTypesToTabValues(filtered[0]?.status ?? ""))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedDataId])

  useEffect(() => {
    setCurrentPage(1)
  }, [tabValue])

  return (
    <Fragment>
      <UploadCSVDialog
        projectId={projectId as string}
        key={dialogKey}
        open={uploadCSVDialogOpen}
        onDialogClose={handleUploadCSVDialogClose}
        setOpen={setUploadCSVDialogOpen}
        setUploadedDataId={setUploadedDataId}
      />

      <Grid container item mt={3}>
        <KonanTabsHeader
          title={t("headers.title")}
          setAction={setUploadCSVDialogOpen}
          containerType={t("headers.containerType")}
          setTabValue={setTabValue}
          value={tabValue}
          searchValue={
            tabValue === "success" ? searchValue : tabValue === "failed" ? searchValueFailed : searchValueInProgress
          }
          setSearchValue={
            tabValue === "success"
              ? setSearchValue
              : tabValue === "failed"
                ? setSearchValueFailed
                : setSearchValueInProgress
          }
          sortValue={tabValue === "success" ? sortValue : tabValue === "failed" ? sortValueFailed : sortValueInProgress}
          setSortValue={
            tabValue === "success" ? setSortValue : tabValue === "failed" ? setSortValueFailed : setSortValueInProgress
          }
          sortOptions={[
            { label: t("sort.mostRecent"), value: "Most Recent" },
            { label: t("sort.name"), value: "Name" },
          ]}
        />
      </Grid>
      <Grid container spacing={2}>
        {isTrainingDataLoading &&
          tabValue === "success" &&
          [1, 2].map((item: number) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <TrainingDataLoader />
            </Grid>
          ))}
        {isTrainingDataLoading &&
          (tabValue === "inProgress" || tabValue === "failed") &&
          [1, 2].map((item: number) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <ProcessingFailedLoader />
            </Grid>
          ))}
      </Grid>

      <Grid container spacing={2} mt={2}>
        {tabValue === "inProgress" &&
          !isTrainingDataLoading &&
          adjustedInProgressTrainings?.length > 0 &&
          adjustedInProgressTrainings
            ?.slice(0, currentPage * pageSize)
            .map((item: TrainingDataUpload, index: number) => (
              <Grid
                item
                key={item.uuid}
                xs={12}
                md={4}
                lg={4}
                ref={
                  adjustedInProgressTrainings.length > currentPage * pageSize && index === currentPage * pageSize - 1
                    ? lastTrainingDataRef
                    : undefined
                }
              >
                <ProcessingFailedCard
                  trainingDataId={item?.uuid}
                  createdAt={item?.created_at}
                  createdBy={item?.created_by}
                  name={item?.name}
                  type="Processing"
                />
              </Grid>
            ))}
        {!isTrainingDataLoading &&
          tabValue === "inProgress" &&
          adjustedInProgressTrainings?.length === 0 &&
          processingTrainingData &&
          processingTrainingData?.length > 0 && (
            <Grid container item xs={12} className={styles.emptySearch}>
              <KonanEmptyStateSearch title={searchValueInProgress} />
            </Grid>
          )}
        {!isTrainingDataLoading && tabValue === "inProgress" && processingTrainingData?.length === 0 && (
          <Grid container xs={12} item className={"empty-container"}>
            <KonanEmptyState
              title={t("empty_states.inProgress.title")}
              buttonText={t("empty_states.inProgress.buttonText")}
              subTitle={t("empty_states.inProgress.subtitle")}
              setAction={handleUploadCSVDialogOpen}
            />
          </Grid>
        )}
        {tabValue === "success" &&
          !isTrainingDataLoading &&
          trainingData &&
          adjustedTrainings &&
          adjustedTrainings?.length > 0 &&
          adjustedTrainings?.slice(0, currentPage * pageSize).map((item: TrainingDataUpload, index: number) => (
            <Grid
              item
              key={item.uuid}
              xs={12}
              md={6}
              lg={4}
              ref={
                adjustedTrainings.length > currentPage * pageSize && index === currentPage * pageSize - 1
                  ? lastTrainingDataRef
                  : undefined
              }
            >
              <TrainingDataCard
                trainingDataId={item?.uuid}
                createdAt={item?.created_at}
                createdBy={item?.created_by}
                name={item?.name}
                models={item?.models}
                status={item?.status}
                isValid={item?.is_valid}
                isLoading={isTrainingDataLoading}
                uuid={item?.uuid}
              />
            </Grid>
          ))}
        {!isTrainingDataLoading &&
          tabValue === "success" &&
          adjustedTrainings?.length === 0 &&
          succeededTrainingData &&
          succeededTrainingData?.length > 0 && (
            <Grid className={styles.emptySearch} direction="column" container xs={12} item>
              <KonanEmptyStateSearch title={searchValue} />
            </Grid>
          )}
        {!isTrainingDataLoading && tabValue === "success" && trainingData && succeededTrainingData?.length === 0 && (
          <Grid container item xs={12} className={"empty-container"}>
            <KonanEmptyState
              title={t("empty_states.success.title")}
              buttonText={t("empty_states.success.buttonText")}
              subTitle={t("empty_states.success.subtitle")}
              setAction={handleUploadCSVDialogOpen}
            />
          </Grid>
        )}
        {tabValue === "failed" &&
          !isTrainingDataLoading &&
          adjustedFailedTrainings &&
          adjustedFailedTrainings?.length > 0 &&
          adjustedFailedTrainings?.slice(0, currentPage * pageSize).map((item: TrainingDataUpload, index: number) => (
            <Grid
              item
              key={item.uuid}
              xs={12}
              md={4}
              lg={4}
              ref={
                adjustedFailedTrainings.length > currentPage * pageSize && index === currentPage * pageSize - 1
                  ? lastTrainingDataRef
                  : undefined
              }
            >
              <ProcessingFailedCard
                trainingDataId={item?.uuid}
                createdAt={item?.created_at}
                createdBy={item?.created_by}
                name={item?.name}
                type="Failed"
              />
            </Grid>
          ))}
        {!isTrainingDataLoading &&
          tabValue === "failed" &&
          adjustedFailedTrainings?.length === 0 &&
          failedTrainingData &&
          failedTrainingData?.length > 0 && (
            <Grid container item xs={12} className={styles.emptySearch}>
              <KonanEmptyStateSearch title={searchValueFailed} />
            </Grid>
          )}
        {!isTrainingDataLoading && tabValue === "failed" && failedTrainingData?.length === 0 && (
          <Grid container xs={12} item className={"empty-container"}>
            <KonanEmptyState
              title={t("empty_states.failed.title")}
              buttonText={t("empty_states.failed.buttonText")}
              subTitle={t("empty_states.failed.subtitle")}
              setAction={handleUploadCSVDialogOpen}
            />
          </Grid>
        )}
      </Grid>

      {hasDataLeft ? (
        <Grid container direction="column" justifyContent="center" alignItems="center" spacing={1}>
          <Box mt={3} />
          <CircularProgress size={36} aria-label={t("loading.circularProgress")} />
          <Box mt={1} />
        </Grid>
      ) : (
        ""
      )}
    </Fragment>
  )
}
